import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { Recycle, Scissors, XCircle } from "lucide-react";
import { toast, Toaster } from "sonner";

const todayStr = () => new Date().toISOString().slice(0, 10);
const monthStart = () => todayStr().slice(0, 8) + "01";

export default function ScrapReport() {
  const [range, setRange] = useState({ start: monthStart(), end: todayStr() });
  const [data, setData] = useState({ rows: [], totals: { mandatory_scrap_kg: 0, rejection_scrap_kg: 0, total_scrap_kg: 0 } });

  const load = useCallback((r) => {
    api.get("/scrap-report", { params: { start: r.start, end: r.end } })
      .then((res) => setData(res.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { load(range); }, [load, range]);

  const t = data.totals || {};
  return (
    <Layout title="Scrap Tracker" subtitle="Mandatory process scrap + rejection scrap (kg) pulled from Daily Production">
      <Toaster richColors position="top-right" />
      <div className="section-card mb-5 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div><label className="block text-sm font-medium text-slate-700 mb-1.5">From</label><input data-testid="scrap-start" type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
        <div><label className="block text-sm font-medium text-slate-700 mb-1.5">To</label><input data-testid="scrap-end" type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
        <div className="kpi-card"><div className="flex items-center gap-2 text-slate-500 text-xs uppercase font-semibold"><Scissors size={14} /> Mandatory Scrap</div><div data-testid="scrap-total-mandatory" className="font-heading text-3xl font-bold text-slate-900 mt-2">{(t.mandatory_scrap_kg || 0).toLocaleString()} <span className="text-base text-slate-400">kg</span></div></div>
        <div className="kpi-card"><div className="flex items-center gap-2 text-slate-500 text-xs uppercase font-semibold"><XCircle size={14} /> Rejection Scrap</div><div data-testid="scrap-total-rejection" className="font-heading text-3xl font-bold text-slate-900 mt-2">{(t.rejection_scrap_kg || 0).toLocaleString()} <span className="text-base text-slate-400">kg</span></div></div>
        <div className="kpi-card kpi-accent"><div className="flex items-center gap-2 text-slate-500 text-xs uppercase font-semibold"><Recycle size={14} /> Total Scrap Available</div><div data-testid="scrap-total-all" className="font-heading text-3xl font-bold text-emerald-700 mt-2">{(t.total_scrap_kg || 0).toLocaleString()} <span className="text-base text-slate-400">kg</span></div></div>
      </div>

      <div className="section-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr><th className="text-left py-3 px-4">Process</th><th className="text-left py-3 px-4">Phase</th><th className="text-right py-3 px-4">Mandatory (kg)</th><th className="text-right py-3 px-4">Rejection (kg)</th><th className="text-right py-3 px-4">Total (kg)</th></tr>
          </thead>
          <tbody data-testid="scrap-table-body">
            {data.rows.length === 0 && <tr><td colSpan="5" className="text-center py-10 text-slate-400">No scrap recorded. Set "Scrap per piece (kg)" on processes and a "Produces" item with unit weight to capture scrap from DPR.</td></tr>}
            {data.rows.map((r) => (
              <tr key={r.process_id} className="border-t border-slate-100 hover:bg-slate-50/50">
                <td className="py-2.5 px-4 font-medium">{r.process_name}</td>
                <td className="py-2.5 px-4">{r.phase}</td>
                <td className="py-2.5 px-4 text-right font-mono">{r.mandatory_scrap_kg.toLocaleString()}</td>
                <td className="py-2.5 px-4 text-right font-mono text-red-600">{r.rejection_scrap_kg.toLocaleString()}</td>
                <td className="py-2.5 px-4 text-right font-mono font-semibold">{r.total_scrap_kg.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
